import { requestPasswordReset } from '../../../utils/requestPasswordReset';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { can } from '../../../utils/permissions';
import { downloadImage } from '../../../utils/upload-image';
import { PLACEHOLDER_PROFILE_NO_PHOTO } from '../../../utils/placeholders';
import { fetchAPIPromise, getServerEndpoint } from '../../../utils/utils';
import DeletionConfirmModal from './DeletionConfirmModal';

function ActionsModal({ user, onClose, onFieldChange, deleteUser, socket, userInfo, COLLA }) {
    const [profilePic, setProfilePic] = useState(null);
    const [showPasswordView, setShowPasswordView] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [copied, setCopied] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletionSummary, setDeletionSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [allowLegacyPasswordClear, setAllowLegacyPasswordClear] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        setAllowLegacyPasswordClear(false);
        fetch(`${getServerEndpoint(COLLA)}/auth/preparation`, { signal: controller.signal })
            .then(response => response.ok ? response.json() : null)
            .then(config => {
                if (!controller.signal.aborted) {
                    setAllowLegacyPasswordClear(config?.passwordAuthRequired === false);
                }
            })
            .catch(() => {});
        return () => controller.abort();
    }, [COLLA]);

    useEffect(() => {
        downloadImage(user.id, COLLA)
            .then(base64 => setProfilePic(base64))
            .catch(() => setProfilePic(false));
    }, [user.id, COLLA]);
    const confirmar_eliminar_user = async () => {
        setLoadingSummary(true);
        setShowDeleteConfirm(true);

        try {
            const response = await fetchAPIPromise(`/user-deletion-summary/${user.id}`);
            setDeletionSummary(response);
        } catch (error) {
            console.error('Failed to fetch deletion summary:', error);
            // Fallback to degraded modal
            setDeletionSummary({
                userId: user.id,
                rehearsalCount: null,
                upcomingEventCount: null,
                positionCount: null,
                hasNotifications: user?.has_notifications || false,
                error: true
            });
        } finally {
            setLoadingSummary(false);
        }
    };

    const force_logout_user = () => {
        const msg = `Vols tancar la sessió de ${user.nom} ${user['primer-cognom']} a tots els dispositius?`;
        if (window.confirm(msg)) {
            socket.emit('.force_logout', { user_id: user.id });
            socket.emit('.request_users');
        }
    };

    const handleDeletePassword = () => {
        const msg = `Borrarem la contrasenya de l'usuari ${user.nom} ${user['primer-cognom']} (${user.mote || '-'}). Un cop torni a obrir l'aplicació, podrà definir la seva nova contrasenya a la pantalla de benvinguda. Vols continuar?`;
        if (window.confirm(msg)) {
            socket.emit('.reload_password', user);
            onClose();
        }
    };

    const toggleHidden = () => {
        onFieldChange(user.id, 'hidden', user.hidden === 1 ? 0 : 1);
    };

    const fullName = [user.nom, user['primer-cognom'], user['segon-cognom']]
        .filter(Boolean)
        .join(' ');

    const handleGeneratePassword = () => {
        const generateRandomPassword = (length = 12) => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%_,.';
            let password = '';
            const array = new Uint32Array(length);
            window.crypto.getRandomValues(array);
            for (let i = 0; i < length; i++) {
                password += chars[array[i] % chars.length];
            }
            return password;
        };

        const confirmMsg = `Estàs segur que vols canviar la contrasenya de ${user.nom} ${user['primer-cognom']}?`;
        if (window.confirm(confirmMsg)) {
            const newPassword = generateRandomPassword(12);
            requestPasswordReset(socket, user.id, newPassword).then(() => {
                setGeneratedPassword(newPassword);
                setShowPasswordView(true);
            }).catch(() => window.alert("No s’ha pogut canviar la contrasenya."));
        }
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(generatedPassword).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
        });
    };

    // Password view
    if (showPasswordView) {
        return createPortal(
            <div className="actions-modal-overlay" onClick={onClose}>
                <div
                    className="actions-modal-content"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'relative',
                        background: 'white',
                        borderRadius: '20px',
                        padding: 0,
                        minWidth: '420px',
                        maxWidth: '90%',
                        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                        overflow: 'hidden',
                    }}
                >
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '16px',
                            right: '16px',
                            background: '#f5f5f7',
                            border: 'none',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            color: '#666',
                            padding: 0,
                            width: '30px',
                            height: '30px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            transition: 'all 0.2s ease',
                            zIndex: 10,
                        }}
                    >
                        ✕
                    </button>

                    <div style={{
                        padding: '24px 24px 20px',
                        textAlign: 'center',
                        borderBottom: '1px solid #f0f0f0',
                    }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: '1.15rem',
                            fontWeight: '600',
                            color: '#1a1a1a',
                        }}>
                            Has canviat la contrasenya a "{user.mote || user.nom}"
                        </h3>
                    </div>

                    <div style={{ padding: '24px' }}>
                        <p style={{
                            margin: '0 0 16px',
                            color: '#666',
                            fontSize: '14px',
                            lineHeight: '1.5',
                        }}>
                            Ara per accedir a la seva compta, haurà de fer servir aquesta:
                        </p>

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            overflow: 'hidden',
                            marginBottom: '20px',
                        }}>
                            <div style={{
                                flex: 1,
                                padding: '14px 16px',
                                fontFamily: 'monospace',
                                fontSize: '18px',
                                fontWeight: '600',
                                letterSpacing: '1px',
                                color: '#1a1a1a',
                            }}>
                                {generatedPassword}
                            </div>
                            <button
                                onClick={handleCopyPassword}
                                disabled={copied}
                                style={{
                                    width: '100px',
                                    padding: '14px',
                                    backgroundColor: copied ? '#4CAF50' : '#3498db',
                                    color: 'white',
                                    border: 'none',
                                    cursor: copied ? 'default' : 'pointer',
                                    fontWeight: '500',
                                    fontSize: '15px',
                                    transition: 'background-color 0.2s ease',
                                }}
                            >
                                {copied ? 'Copiat!' : 'Copiar'}
                            </button>
                        </div>

                        <div style={{
                            fontSize: '14px',
                            color: '#666',
                            lineHeight: '1.5',
                        }}>
                            <p style={{ margin: '0 0 12px' }}>
                                <strong>Important:</strong> Quan tanquis aquesta finestra, ja no podràs tornar a veure la contrasenya.
                            </p>
                            <p style={{ margin: 0 }}>
                                La contrasenya s'ha generat aleatòriament amb seguretat criptogràfica i ja s'ha guardat al sistema.
                            </p>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    const handleConfirmDelete = () => {
        deleteUser(user);
        setShowDeleteConfirm(false);
        onClose();
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setDeletionSummary(null);
    };

    if (showDeleteConfirm) {
        return createPortal(
            <DeletionConfirmModal
                user={user}
                deletionSummary={deletionSummary}
                loadingSummary={loadingSummary}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                COLLA={COLLA}
            />,
            document.body
        );
    }

    return createPortal(
        <div className="actions-modal-overlay" onClick={onClose}>
            <div className="actions-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-button" onClick={onClose}>✕</button>
                <div className="actions-modal-header">
                    <h3>Accions</h3>
                </div>

                <div className="actions-modal-user-info">
                    <div className="user-avatar-large">
                        {profilePic === null ? (
                            <div className="avatar-loading">...</div>
                        ) : (
                            <img
                                src={profilePic || PLACEHOLDER_PROFILE_NO_PHOTO}
                                alt={fullName}
                            />
                        )}
                    </div>
                    <div className="user-details">
                        <div className="actions-modal-user-name">{fullName}</div>
                        {user.mote ? <div className="user-mote">"{user.mote}"</div> : null}
                    </div>
                </div>

                <div className="actions-modal-body">
                    <div className="actions-modal-section-title">Canviar estat</div>
                    
                    <button
                        className="action-button action-toggle-hidden"
                        onClick={() => {
                            toggleHidden();
                            onClose();
                        }}
                    >
                        <span style={{ fontSize: '1.1em' }}>{user.hidden === 1 ? '👁️' : '🙈'}</span>
                        {user.hidden === 1 ? 'Desamagar casteller' : 'Amagar casteller'}
                    </button>

                    {can(userInfo, 'MANAGE_USER_PASSWORDS') ? (
                        <div className="actions-modal-section-title">Gestionar contrasenya</div>
                    ) : null}

                    {can(userInfo, 'MANAGE_USER_PASSWORDS') ? (
                        <button
                            className="action-button action-reset-password"
                            onClick={handleGeneratePassword}
                        >
                            <span style={{ fontSize: '1.1em' }}>🔑</span>
                            Generar nova contrasenya
                        </button>
                    ) : null}

                    {allowLegacyPasswordClear && can(userInfo, 'MANAGE_USER_PASSWORDS') ? (
                        <button
                            className="action-button action-delete-password"
                            onClick={handleDeletePassword}
                        >
                            <span style={{ fontSize: '1.1em' }}>🧽</span>
                            Borrar contrasenya
                        </button>
                    ) : null}

                    {(can(userInfo, 'DELETE_USER') || can(userInfo, 'MANAGE_USER_SESSIONS')) ? (
                        <div className="actions-modal-section-title">Zona de perill</div>
                    ) : null}

                    {can(userInfo, 'MANAGE_USER_SESSIONS') ? (
                        <button
                            className="action-button action-force-logout"
                            onClick={() => {
                                force_logout_user();
                                onClose();
                            }}
                        >
                            <span style={{ fontSize: '1.1em' }}>🚪</span>
                            Tancar sessió a tots els dispositius
                        </button>
                    ) : null}

                    {can(userInfo, 'DELETE_USER') ? (
                        <button
                            className="action-button action-delete"
                            onClick={confirmar_eliminar_user}
                        >
                            <span style={{ fontSize: '1.1em' }}>🗑️</span>
                            Eliminar usuari
                        </button>
                    ) : null}
                </div>
            </div>
        </div>,
        document.body
    );
}

export default ActionsModal;
